import React from 'react'
import * as Sentry from '@sentry/react'
import { useDantiAuth } from '@/hooks/use-danti-auth.ts'
import {
  type UnshareCollectionParams,
  useCollectionList,
  useCreateCollection,
  useRefreshCollections,
  useRemoveCollection,
  useShareCollectionWithVendor,
  useUnshareCollectionWithVendor,
  useUpdateCollection,
} from '@/utils/data-fetching/api/collections'
import type { Collection } from '@/utils/types/collection-types.ts'
import { singletonHook } from 'react-singleton-hook'
import { getRequest } from '@/utils/data-fetching/config.ts'
export type ShareRoleType = 'viewer' | 'editor' | 'owner' | 'creator'

export type CollectionSortType =
  | ''
  | 'alpha-asc'
  | 'alpha-desc'
  | 'date-asc'
  | 'date-desc'

export interface ListCollectionsParams {
  next?: string
  prev?: string
  limit?: number
  sort?: CollectionSortType
  term?: string
}

export interface CollectionsResponse {
  collections: Collection[]
  next: string
  prev: string
  total: number
}

export interface CreateCollectionParams {
  name: string
  desc: string
  tags?: string[]
}

export interface ShareCollectionParams {
  id: string
  emails: string[]
  message: string
  role: ShareRoleType
}

export interface UpdateCollectionParams {
  id: string
  name?: string
  desc?: string
  tags?: string[]
}

interface UseCollectionsHookResponse {
  collections: Collection[]
  totalCount: number
  isLoading: boolean
  pageSize: number
  currentPage: number

  sort: CollectionSortType
  setSort: (sort: CollectionSortType) => void
  nextPage: () => void
  previousPage: () => void
  create: (params: CreateCollectionParams) => Promise<unknown>
  shareWithVendor: (params: ShareCollectionParams) => Promise<unknown>
  unShareWithVendor: (params: UnshareCollectionParams) => Promise<unknown>
  update: (params: UpdateCollectionParams) => Promise<unknown>
  remove: (id: string) => Promise<unknown>
  refresh: () => void
  search: (term: string) => void
}

const useCollectionHookImpl = (): UseCollectionsHookResponse => {
  const { isAuthenticated } = useDantiAuth()

  const [currentPage, setCurrentPage] = React.useState(0)

  const [nextToken, setNextToken] = React.useState('')
  const [previousToken, setPreviousToken] = React.useState('')
  const [sort, setSort] = React.useState<CollectionSortType>('')
  const [searchTerm, setSearchTerm] = React.useState('')

  const pageSize = 50

  const { data, isLoading } = useCollectionList({
    nextToken,
    previousToken,
    sort,
    searchTerm,
    pageSize,
    isAuthenticated,
  })

  const totalCount = React.useMemo(() => data?.total ?? 0, [data])
  const maxPages = React.useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount],
  )

  const refreshCollections = useRefreshCollections()
  const createCollection = useCreateCollection()
  const shareCollectionWithVendor = useShareCollectionWithVendor()
  const unShareCollectionWithVendor = useUnshareCollectionWithVendor()
  const updateCollection = useUpdateCollection()
  const removeCollection = useRemoveCollection()

  const nextPage = () => {
    if (!data?.next) {
      return
    }
    setPreviousToken('')
    setNextToken(data.next)
    setCurrentPage((page) => Math.min(maxPages, page + 1))
  }

  const previousPage = () => {
    if (!data?.prev) {
      return
    }
    setNextToken('')
    setPreviousToken(data.prev)
    setCurrentPage((page) => Math.max(0, page - 1))
  }

  const search = React.useCallback((term: string) => {
    setNextToken('')
    setPreviousToken('')
    setCurrentPage(0)
    setSearchTerm(term)
  }, [])

  return {
    collections: data?.collections ?? [],
    totalCount: data?.total ?? 0,
    isLoading,
    pageSize,
    currentPage,
    nextPage,
    previousPage,
    sort,
    setSort,
    create: createCollection,
    shareWithVendor: shareCollectionWithVendor,
    unShareWithVendor: unShareCollectionWithVendor,
    update: updateCollection,
    remove: removeCollection,
    refresh: refreshCollections,
    search,
  }
}

export const useCollections = singletonHook<UseCollectionsHookResponse>(
  () => ({
    collections: [],
    totalCount: 0,
    isLoading: false,
    pageSize: 0,
    currentPage: 0,
    sort: '',
    setSort: () => {},
    nextPage: () => {},
    previousPage: () => {},
    create: async () => {},
    shareWithVendor: async () => {},
    unShareWithVendor: async () => {},
    update: async () => {},
    remove: async () => {},
    refresh: () => {},
    search: () => {},
  }),
  useCollectionHookImpl,
)

export interface SavedSearchLocationsDownloadProps {
  collectionId: string
  collectionName?: string
  totalCount?: number
  filter?: string | undefined
  searchTerm?: string | undefined
}

export const handleCollectionLocationDownload = ({
  collectionId,
  collectionName,
  totalCount,
  filter,
  searchTerm,
}: SavedSearchLocationsDownloadProps) => {
  return void getRequest(
    `collection/${collectionId}/items/download?type=extent${filter ? `&filter=${filter}` : ''}${totalCount ? `&limit=${totalCount}` : ''}${searchTerm ? `&term=${searchTerm}` : ''}`,
    {
      responseType: 'blob', // Important to specify the response type as blob
    },
  )
    .then((response) => {
      let downloadName = `${collectionName}.json`
      if (response.headers['content-disposition']) {
        const contentDisposition = response.headers[
          'content-disposition'
        ] as string
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch && filenameMatch.length > 1) {
          downloadName = filenameMatch[1]
        }
      }
      const url = window.URL.createObjectURL(
        new Blob([response.data as BlobPart]),
      )
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = downloadName
      document.body.append(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
    })
    .catch(() => {
      Sentry.captureMessage('Failed to download extents', {
        extra: {
          collectionId,
          filter,
          totalCount,
        },
      })
    })
}
